import type { ElementPart } from 'lit'

import { Directive, directive } from 'lit/directive.js'
import { nothing } from 'lit'

const NON_BUBBLING_EVENTS = [
	'load',
	'unload',
	'scroll',
	'focus',
	'blur',
	'loadstart',
	'progress',
	'error',
	'abort',
	'load',
	'loadend'
]

const FOCUS = Symbol('focusin')
const BLUR = Symbol('focusout')

// Not all events bubble but they do have bubbling counterparts so we need to
// alias them and keep track of the original event name. These mappings are used
// to lookup event aliases using a symbol that allows us reverse lookup the
// original event name once the (aliased) event is captured.
const EVENT_ALIASES = { focus: FOCUS, blur: BLUR }
const ALIASED_EVENTS = { focusin: FOCUS, focusout: BLUR }
const EVENT_ALIAS_NAMES = { [FOCUS]: 'focus', [BLUR]: 'blur' }

const elements = new WeakMap<
	ShadowRoot,
	Map<string | symbol, WeakSet<Element>>
>()

export class DelegatedEvent<T extends Event = Event> extends CustomEvent<T> {
	#event: T

	constructor(name: string, event: T) {
		super(name, {
			detail: event,
			composed: true,
			bubbles: !NON_BUBBLING_EVENTS.includes(name)
		})
		this.#event = event
	}

	get defaultPrevented() {
		return this.#event.defaultPrevented
	}

	preventDefault() {
		this.#event.preventDefault()
	}
}

class EventDelegationDirective extends Directive {
	#before: Record<string, (event: Event) => void> = {}
	#after: Record<string, (event: Event) => void> = {}

	#listener = {
		handleEvent: (event: Event) => {
			const shadowRoot = event.currentTarget as ShadowRoot

			// Lookup registered targets for this event type
			const key = ALIASED_EVENTS[event.type] || event.type
			const state = elements.get(shadowRoot)
			const targets = state?.get(key)
			if (!targets) return

			// Traverse up the DOM to find the first registered target
			let target = event.target as Element
			while (target) {
				if (targets.has(target)) {
					// Create a new event of the original event type
					const name = EVENT_ALIAS_NAMES[event.type] || event.type
					const delegatedEvent = new DelegatedEvent(name, event)

					// Prevent the event from bubbling outside the shadow root
					event.stopPropagation()

					const before = this.#before?.[event.type]
					if (before) before(event)

					// Dispatch the delegated event to the custom element
					shadowRoot.host.dispatchEvent(delegatedEvent)

					const after = this.#after?.[event.type]
					if (after) after(event)

					// Avoid emitting the same event multiple times for nested listeners
					break
				}
				target = target.parentElement
			}
		}
	} as EventListenerObject

	render(
		_events: string[],
		_options?: AddEventListenerOptions & {
			before?: { [event: string]: (event: Event) => void }
			after?: { [event: string]: (event: Event) => void }
		}
	) {
		return nothing
	}

	update(
		part: ElementPart,
		[events, options = {}]: Parameters<this['render']>
	) {
		const host = part.options.host as Element
		const root = host.shadowRoot as ShadowRoot

		const { before, after, ...eventListenerOptions } = options
		this.#before = before
		this.#after = after

		let state = elements.get(root)
		if (!state) elements.set(root, (state = new Map()))

		for (const event of events) {
			// Lookup possible event aliases
			const key = EVENT_ALIASES[event] || event
			const targets = state.get(key)

			if (targets) {
				targets.add(part.element)
			} else {
				state.set(key, new WeakSet([part.element]))

				// Attach the listener to the shadow root using capture to ensure that
				// any mutations or method calls are captured by the delegated event
				root.addEventListener(event, this.#listener, {
					capture: true,
					...eventListenerOptions
				})
			}
		}
	}
}

/**
 * Delegate events to the host element as instances of DelegatedEvent that
 * mirror the original event and can be used to prevent the default action of
 * the original event.
 *
 * @example
 * class MyComponent extends LfuiElement {
 *   render() {
 *     return html`
 *       <button ${delegateEvents(['click', 'dblclick'])}>Click me</button>
 *     `
 *   }
 * }
 *
 * @param events - The (native) events to delegate.
 * @param options - Event listener options with extras
 * @param options.before - A map of function to call per event type before the event is delegated
 * @param options.after - A map of function to call per event type after the event is delegated
 */
export default directive(EventDelegationDirective)
